import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, SearchInput } from '@c2fo/liquidity';
import { TechnicalDifficultyIcon } from '@c2fo/liquidity/icons';
import { AdjustmentInvoiceFetchOptions } from '@/enterprise/data/useAdjustmentInvoices';
import useAdjustmentInvoiceStats from '@/enterprise/data/useAdjustmentInvoiceStats';
import useDataDog from '@/reporting/DataDog';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import { useCursorPagination } from '@/utils/useCursorPagination';
import useLocaleFormat from '@/utils/useLocaleFormat';
import useUrlState from '@/utils/useUrlState';
import NoDataState from '../../NoDataState';
import Skeleton from '../../Skeleton';
import { Table, TableBody, TableDisplaySkeletonRows, TablePagination } from '../../Table';
import { useInvoiceCentralLayoutContext } from '../InvoiceCentralLayout';
import AdjustmentFilters, { AdjustmentFilterOptions } from './AdjustmentFilters';
import AdjustmentsTable from './AdjustmentsTable';
import { mapUrlStateToApiSearchFilters, mergeFilters } from './utils';

export type AdjustmentUrlStateSortKey = NonNullable<AdjustmentInvoiceFetchOptions>['sortBy'];

export interface AdjustmentUrlState {
  pageSize: number;
  searchValue?: string;
  filters?: AdjustmentFilterOptions;
  sort: {
    key: AdjustmentUrlStateSortKey;
    direction: 'asc' | 'desc';
  };
}

const AdjustmentsAmount = ({ urlState }: { urlState: AdjustmentUrlState }) => {
  const { asCurrency, asNumber } = useLocaleFormat();
  const { t } = useTranslation();

  const {
    buyer: { takerMarkets },
    urlParams: { makerOrganizationUuid, currency },
  } = useInvoiceCentralLayoutContext();

  const search = useMemo(() => mapUrlStateToApiSearchFilters({ urlState }), [urlState]);

  const { data: adjustmentInvoiceStats } = useAdjustmentInvoiceStats({
    makerOrganizationUuid,
    takerMarkets,
    ...(!!search && {
      search,
    }),
  });

  return (
    <div>
      {asCurrency(adjustmentInvoiceStats?.totalAmount ?? 0, currency)} (
      {t('core.invoiceWithCount', {
        count: adjustmentInvoiceStats?.totalCount,
        formattedCount: asNumber({ value: adjustmentInvoiceStats?.totalCount }),
      })}
      )
    </div>
  );
};

const Adjustment = () => {
  const { t } = useTranslation();
  const [searchInput, setSearchInput] = useState<string>('');
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [filterOpen, setFilterOpen] = useState<boolean>(false);
  const { currentCursor, currentPage, canGoBack, goToNextPage, goToPreviousPage, reset } = useCursorPagination();
  const { addAction } = useDataDog();

  const [urlState, setUrlState] = useUrlState<AdjustmentUrlState>({
    pageSize: 25,
    sort: {
      key: 'amount',
      direction: 'desc',
    },
  });

  const {
    urlParams: { makerOrganizationUuid, currency },
  } = useInvoiceCentralLayoutContext();

  const handleSearchValue = (search: string | undefined) => {
    addAction('invoice-central::invoices-searched', {
      invoiceType: 'adjustment',
      makerOrganizationUuid,
      currency,
    });
    setUrlState((f) => ({
      ...f,
      searchValue: search?.trim(),
    }));
    reset();
  };

  const handleSort = (sortKey: AdjustmentUrlState['sort']['key']) => {
    const updatedSortDirection = urlState.sort?.direction === 'asc' ? 'desc' : 'asc';
    addAction('invoice-central::invoices-sorted', {
      invoiceType: 'adjustment',
      sortKey,
      sortDirection: updatedSortDirection,
      makerOrganizationUuid,
      currency,
    });
    if (urlState.sort && sortKey === urlState.sort.key) {
      setUrlState((f) => ({
        ...f,
        sort: {
          key: sortKey,
          direction: updatedSortDirection,
        },
      }));
    } else {
      setUrlState((f) => ({
        ...f,
        sort: {
          key: sortKey,
          direction: 'desc',
        },
      }));
    }

    reset();
  };

  const handleNextPage = () => {
    if (nextCursor) {
      goToNextPage(nextCursor);
    }
  };

  const handlePreviousPage = () => {
    goToPreviousPage();
  };

  const handleFilters = (newFilters?: AdjustmentFilterOptions) => {
    addAction('invoice-central::invoices-filtered', {
      invoiceType: 'adjustment',
      makerOrganizationUuid,
      currency,
      filters: Object.keys(newFilters ?? {}),
    });

    setUrlState((f) => {
      return {
        ...f,
        filters: mergeFilters({ prevFilters: f.filters, newFilters }),
      };
    });
    reset();
  };

  const clearFilters = () => {
    setUrlState((f) => {
      const updatedState: Partial<AdjustmentUrlState> = {
        ...f,
        filters: undefined,
      };

      return updatedState;
    });
    reset();
  };

  return (
    <div>
      <AdjustmentFilters
        filterOpen={filterOpen}
        setFilterOpen={setFilterOpen}
        handleFilters={handleFilters}
        currency={currency}
        filters={urlState.filters}
      />
      <div className="border-y border-deepGray-500/[12%] bg-canvas">
        <div className="flex flex-col justify-between sm:flex-row">
          {/* Search Bar */}
          <div className="flex flex-col items-center gap-3 p-4 sm:flex-row">
            <SearchInput
              className="w-full sm:w-60"
              onChange={(e) => setSearchInput(e.target.value)}
              onClear={() => {
                setSearchInput('');
                handleSearchValue(undefined);
              }}
              placeholder={t('invoices.searchInputPlaceholder')}
              value={searchInput}
              withSearchIcon
              onKeyDown={(e) => e.key === 'Enter' && handleSearchValue(searchInput)}
            />
            <Button
              className="w-full bg-canvas sm:w-fit"
              onClick={() => handleSearchValue(searchInput)}
              variant="secondary"
            >
              {t('core.search')}
            </Button>
          </div>
          {/* Filters */}
          <div className="flex items-center gap-2 p-4">
            <div className="relative">
              <Button onClick={() => setFilterOpen(true)} variant="primary">
                {t('core.filters')}
              </Button>
              {Object.keys(urlState.filters ?? {}).length > 0 && (
                <div className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-lightBlue-500 text-xs font-medium text-white">
                  {Object.keys(urlState.filters ?? {}).length}
                </div>
              )}
            </div>
            <Button
              disabled={Object.keys(urlState.filters ?? {}).length === 0}
              onClick={clearFilters}
              variant="secondary"
            >
              {t('core.clearFilters')}
            </Button>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1 pb-4 pl-4">
          <div className="font-medium">{t('core.ineligibleAmount')}:</div>
          <QueryBoundaries LoadingComponent={() => <Skeleton className="h-6 w-24" />}>
            <AdjustmentsAmount urlState={urlState as AdjustmentUrlState} />
          </QueryBoundaries>
        </div>
      </div>
      <QueryBoundaries
        LoadingComponent={() => (
          <Table>
            <TableBody>
              <TableDisplaySkeletonRows colSpan={5} rowCount={10} />
            </TableBody>
          </Table>
        )}
        ErrorComponent={() => (
          <div className="px-4 pb-6 pt-8">
            <NoDataState
              icon={<TechnicalDifficultyIcon className="size-24 pb-4" />}
              title={t('invoices.errorLoadingInvoices')}
              message={t('invoices.errorLoadingInvoicesDescription')}
              action={
                <Button size="sm" variant="ancillary" onClick={() => window.location.reload()}>
                  {t('core.refresh')}
                </Button>
              }
            />
          </div>
        )}
      >
        <AdjustmentsTable
          currentCursor={currentCursor}
          setNextCursor={setNextCursor}
          urlState={urlState as AdjustmentUrlState}
          handleSort={handleSort}
        />
      </QueryBoundaries>
      <TablePagination
        className="p-6"
        onNextChange={handleNextPage}
        onPrevChange={handlePreviousPage}
        onPageSizeChange={(e) => setUrlState((f) => ({ ...f, pageSize: Number(e.target.value) }))}
        pageSize={urlState.pageSize ?? 25}
        disablePrev={!canGoBack}
        disableNext={!nextCursor}
        currentPage={currentPage}
      />
    </div>
  );
};

export default Adjustment;
